"use client";

import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { getErrorDisplay, getExplorerTxUrl, getStatusDisplay } from "../../../../anyspend/index.js";
import { useAnyspendOrderAndTransactions } from "../../../../anyspend/react/hooks/useAnyspendOrderAndTransactions.js";
import { useOnOrderSuccess } from "../../../../anyspend/react/hooks/useOnOrderSuccess.js";
import { TextShimmer } from "../../../../global-account/react/index.js";
import { cn } from "../../../../shared/utils/cn.js";
import { formatNumber } from "../../../../shared/utils/formatNumber.js";
import { ExternalLink, Loader2, RefreshCcw } from "lucide-react";
import { motion } from "motion/react";
import { useEffect, useRef } from "react";
import { useAnySpendCustomization } from "../context/AnySpendCustomizationContext.js";
import { OrderStatus } from "../common/OrderStatus.js";
export function CheckoutOrderStatus({
  orderId,
  themeColor,
  returnUrl,
  returnLabel,
  onSuccess,
  onError,
  onRetry,
  showPoints = false,
  showOrderId = false,
  classes
}) {
  // Stable refs for callback props to avoid re-triggering effects
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const {
    orderAndTransactions,
    isLoadingOrderAndTransactions
  } = useAnyspendOrderAndTransactions(orderId);
  const order = orderAndTransactions?.data?.order;
  const executeTx = orderAndTransactions?.data?.executeTx;
  const points = orderAndTransactions?.data?.points;
  const {
    content
  } = useAnySpendCustomization();
  // Fire onSuccess when order reaches "executed"
  useOnOrderSuccess({
    orderData: orderAndTransactions,
    orderId,
    onSuccess: txHash => onSuccess?.({
      orderId,
      txHash
    })
  });
  // Fire onError once for terminal failure states
  const onErrorCalled = useRef(false);
  useEffect(() => {
    if (!order || onErrorCalled.current) return;
    if (order.status === "failure" || order.status === "expired" || order.status === "refunded") {
      const {
        description
      } = getStatusDisplay(order);
      onErrorRef.current?.(new Error(description || `Order ${order.status}`));
      onErrorCalled.current = true;
    }
  }, [order]);
  // Reset error flag if orderId changes
  const prevOrderId = useRef(orderId);
  useEffect(() => {
    if (prevOrderId.current !== orderId) {
      onErrorCalled.current = false;
      prevOrderId.current = orderId;
    }
  }, [orderId]);
  // Clear persisted orderId when order reaches a terminal state
  const orderStatus = order?.status;
  useEffect(() => {
    if (orderStatus && ["executed", "failure", "expired", "refunded"].includes(orderStatus)) {
      sessionStorage.removeItem("anyspend_checkout_orderId");
    }
  }, [orderStatus]);
  // Loading state
  if (isLoadingOrderAndTransactions || !order) {
    return _jsxs("div", {
      className: cn("flex flex-col items-center gap-3 py-12", classes?.orderStatusPanel),
      children: [_jsx(Loader2, {
        className: "h-6 w-6 animate-spin text-gray-400"
      }), _jsx(TextShimmer, {
        duration: 1.5,
        className: "text-sm",
        children: "Loading order status..."
      })]
    });
  }
  const isExecuted = order.status === "executed";
  const isRefunding = order.status === "refunding";
  return _jsxs(motion.div, {
    initial: {
      opacity: 0,
      y: 10
    },
    animate: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 0.3,
      ease: "easeOut"
    },
    className: cn("anyspend-checkout-order-status flex flex-col items-center gap-5 py-6", classes?.orderStatusPanel),
    children: [_jsx(OrderStatus, {
      order: order
    }), (showPoints || showOrderId) && _jsx(motion.div, {
      initial: {
        opacity: 0,
        y: 8
      },
      animate: {
        opacity: 1,
        y: 0
      },
      transition: {
        duration: 0.25,
        delay: 0.15
      },
      className: "bg-as-surface-secondary border-as-border-secondary w-full rounded-xl border px-4 py-3",
      children: _jsxs("div", {
        className: "flex flex-col gap-2 text-sm",
        children: [showPoints && points != null && points > 0 && _jsxs(_Fragment, {
          children: [_jsxs("div", {
            className: "flex w-full justify-between",
            children: [_jsx("span", {
              className: "text-as-tertiary",
              children: "Points"
            }), _jsxs("span", {
              className: "text-as-brand font-semibold",
              children: ["+", formatNumber(points), " pts"]
            })]
          }), showOrderId && _jsx("div", {
            className: "divider w-full"
          })]
        }), showOrderId && _jsxs("div", {
          className: "flex w-full items-center justify-between gap-3",
          children: [_jsx("span", {
            className: "text-as-tertiary shrink-0",
            children: "Order ID"
          }), _jsx("span", {
            className: "text-as-primary min-w-0 overflow-hidden text-ellipsis whitespace-nowrap",
            children: order.id
          })]
        })]
      })
    }), isExecuted && executeTx && _jsxs(motion.a, {
      initial: {
        opacity: 0
      },
      animate: {
        opacity: 1
      },
      transition: {
        duration: 0.3,
        delay: 0.3,
        ease: "easeOut"
      },
      href: getExplorerTxUrl(order.dstChain, executeTx.txHash),
      target: "_blank",
      rel: "noopener noreferrer",
      className: cn("flex items-center gap-1.5 text-sm text-blue-600 hover:underline dark:text-blue-400", classes?.transactionLink),
      children: ["View Transaction", _jsx(ExternalLink, {
        className: "h-3.5 w-3.5"
      })]
    }), order.status === "failure" && order.errorDetails && _jsx(motion.p, {
      initial: {
        opacity: 0
      },
      animate: {
        opacity: 1
      },
      transition: {
        duration: 0.2,
        delay: 0.2
      },
      className: "max-w-[40ch] text-center text-sm text-red-500 dark:text-red-400",
      children: getErrorDisplay(order.errorDetails)
    }), (order.status === "failure" || order.status === "expired") && onRetry && _jsxs(motion.button, {
      initial: {
        opacity: 0,
        y: 6
      },
      animate: {
        opacity: 1,
        y: 0
      },
      transition: {
        duration: 0.25,
        delay: 0.3
      },
      onClick: onRetry,
      className: cn("flex items-center gap-2 rounded-xl px-6 py-3 text-sm font-medium transition-colors", "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-neutral-800 dark:text-gray-300 dark:hover:bg-neutral-700", classes?.retryButton),
      children: [_jsx(RefreshCcw, {
        className: "h-4 w-4"
      }), content.retryButtonLabel || "Try Again"]
    }), (isExecuted || order.status === "refunded") && returnUrl && _jsx(motion.a, {
      initial: {
        opacity: 0,
        y: 6
      },
      animate: {
        opacity: 1,
        y: 0
      },
      transition: {
        duration: 0.25,
        delay: 0.4
      },
      href: returnUrl,
      className: cn("inline-flex rounded-xl px-6 py-3 text-sm font-medium transition-colors", classes?.returnButton),
      style: {
        backgroundColor: themeColor || "#111827",
        color: "#fff"
      },
      children: content.returnButtonLabel || returnLabel || "Return to Store"
    }), isRefunding && _jsx("p", {
      className: "text-sm text-gray-500 dark:text-gray-400",
      children: "Please wait while your funds are being returned."
    })]
  });
}